import React, { useMemo, useState, useCallback } from 'react';
import moment from 'moment';
import Button from '@atlaskit/button';
import { getFormattedDateString } from '@css-xp/base';
import CalendarIcon from '@atlaskit/icon/glyph/calendar';
import SelectClearIcon from '@atlaskit/icon/glyph/select-clear';
import { CalendarDialog } from './components/calendar-dialog';
import { useCartProducts, useProductsForCloudSiteStore, useStore } from '../../../store';
import { getValidNextBillDateRange } from './utils/get-valid-next-bill-date-range';
import { isJiraProduct } from '@atlassian/cssxp-utils-product';
import { useOpenClose } from '@css-xp/ppc-custom-hooks';
import { NextBillDateWarningModal } from './components/next-bill-date-warning-modal';

const isDateDisabled = (date: string) => {
  const d = moment(date).date();

  return d > 28;
};

export const NextBillDateWrapper = ({ cloudSiteHostname }: { cloudSiteHostname?: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { isOpen: isCobillModalOpen, open: openCobillModal, close: closeCobillModal } = useOpenClose();
  const [{ nextBillDate }, { setNextBillDate, clearNextBillDate }] = useStore();
  const selectedProducts = useCartProducts();

  const existingProductsForCloudSite = useProductsForCloudSiteStore({
    cloudSiteHost: cloudSiteHostname as string,
  });

  const allJiraProductsUnderTheCloudSite = existingProductsForCloudSite.filter((product) =>
    isJiraProduct(product.productId)
  );

  const productsWhoseNextBillDateIsBeingSet = useMemo(() => {
    if (!cloudSiteHostname) return selectedProducts;

    return selectedProducts.filter(({ cloudSite }) => cloudSite === cloudSiteHostname);
  }, [cloudSiteHostname, selectedProducts]);

  const numberOfJiraProductsInTheCart = productsWhoseNextBillDateIsBeingSet.filter((product) =>
    isJiraProduct(product.productId)
  ).length;

  const hasAllJiraProductsBeenAdded =
    // If the site doesn't have any jira products
    allJiraProductsUnderTheCloudSite.length === 0 ||
    // Even if the site has Jira products, but none has been added to the cart
    numberOfJiraProductsInTheCart === 0 ||
    // If there are Jira products in both the site and the cart, the cart should have all of them or none.
    numberOfJiraProductsInTheCart === allJiraProductsUnderTheCloudSite.length;

  const validNextBillDateRange = getValidNextBillDateRange(productsWhoseNextBillDateIsBeingSet);

  const selectedTimestamp = useMemo(() => {
    if (cloudSiteHostname) {
      return nextBillDate.cloudSiteLevel?.[cloudSiteHostname];
    }

    return nextBillDate.cartLevel;
  }, [cloudSiteHostname, nextBillDate.cartLevel, nextBillDate.cloudSiteLevel]);

  const setSelectedTimestamp = useCallback(
    (nextBillDateTimestamp: string) => {
      setNextBillDate(nextBillDateTimestamp, cloudSiteHostname);
    },
    [cloudSiteHostname, setNextBillDate]
  );
  const openModal = useCallback(() => setIsOpen(true), []);
  const closeModal = useCallback(() => setIsOpen(false), []);

  return (
    <>
      <Button
        iconBefore={<CalendarIcon label="" />}
        onClick={() => {
          if (!hasAllJiraProductsBeenAdded) {
            openCobillModal();
            return;
          }
          openModal();
        }}
        isDisabled={selectedProducts.length === 0}
        testId="uof.btn.select-next-billing-date"
      >
        {!selectedTimestamp
          ? 'Select billing date'
          : `Billing date: ${getFormattedDateString({ date: selectedTimestamp })}`}
      </Button>
      {selectedTimestamp && (
        <Button iconBefore={<SelectClearIcon label="" />} onClick={() => clearNextBillDate(cloudSiteHostname)} />
      )}
      {isOpen && (
        <CalendarDialog
          closeModal={closeModal}
          onSelect={setSelectedTimestamp}
          selectedTimestamp={selectedTimestamp}
          validNextBillDateRange={validNextBillDateRange}
          disabledDateFilter={isDateDisabled}
        />
      )}
      {isCobillModalOpen && <NextBillDateWarningModal closeModal={closeCobillModal} />}
    </>
  );
};
